import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { LogIn, Eye, EyeOff, Phone } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_baduwork-tracker/artifacts/8tdmywqb_Badu%20Facility%20Services.png";

export default function LoginPage() {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(phone, password);
    
    if (result.success) {
      navigate(result.user.role === "admin" ? "/admin" : "/worker");
    }
    
    setLoading(false);
  };

  const formatPhoneDisplay = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");
    // Format as XXX XXX XXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits and spaces
    const cleaned = value.replace(/[^\d\s]/g, "");
    setPhone(cleaned.replace(/\s/g, "")); // Store without spaces
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="relative z-10 w-full max-w-md animate-slide-up">
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Badu Facility Services" 
            className="h-24 mx-auto mb-4"
            data-testid="login-logo"
          />
          <h1 className="text-3xl font-bold text-white font-manrope">PONTO FACIL</h1>
          <p className="text-white/80 mt-2">Sistema de Controle de Ponto</p>
        </div>

        <Card className="bg-white border-0 shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl font-semibold text-gray-800">Entrar</CardTitle>
            <CardDescription className="text-gray-500">
              Digite seu número de celular e senha
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-700">Número de Celular</Label>
                <div className="flex gap-2">
                  <div className="flex items-center justify-center px-3 bg-gray-100 border border-gray-200 rounded-md text-gray-600 font-medium">
                    +41
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder=""
                      value={formatPhoneDisplay(phone)}
                      onChange={handlePhoneChange}
                      required
                      maxLength={11}
                      className="bg-gray-50 border-gray-200 text-gray-800 placeholder:text-gray-400 focus:border-[#F5A623] focus:ring-[#F5A623] pl-10"
                      data-testid="login-phone-input"
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-800 placeholder:text-gray-400 focus:border-[#F5A623] focus:ring-[#F5A623] pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary text-white font-semibold py-5"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <div className="spinner w-5 h-5 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Entrar
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-gray-100">
              <a 
                href="https://www.badufacility.ch/pontofacil" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block text-center text-sm text-[#2ECC40] hover:text-[#F5A623] transition-colors"
              >
                www.badufacility.ch/pontofacil
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
